/**
 * Auth context — real Supabase session listener (Phase 10b).
 *
 * Exports AuthProvider (component) and AuthContext + AuthContextValue (types).
 * The useSession() hook lives in lib/use-session.ts to satisfy react-refresh
 * fast-refresh rules (hooks + components cannot share a file).
 *
 * AuthProvider does two things on mount:
 *   1. Hydrate: call supabase.auth.getSession() once, flip `loading: false`.
 *   2. Subscribe: register a supabase.auth.onAuthStateChange listener and
 *      update the context value whenever the session changes (sign-in,
 *      sign-out, token refresh).
 *
 * Children must wait on `loading` before deciding to redirect — otherwise the
 * initial render flashes "no session → /auth/login" before hydration finishes.
 *
 * StrictMode in main.tsx double-invokes effects in development; the mounted
 * guard prevents the second mount from clobbering state from the first.
 */

import { createContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import type { Session, User } from '@supabase/supabase-js';
import { supabase } from './supabase';
import { resetOnboardingStoresOnUserChange } from './onboarding-reset';
import { setQueryPrincipal } from './query-client';

export interface AuthContextValue {
  /** Currently authenticated session, or null if signed out / not yet loaded. */
  session: Session | null;
  /** Convenience accessor — `session?.user ?? null`. */
  user: User | null;
  /**
   * Role claim derived from the access_token's `app_metadata.role` on the
   * Supabase user. Falls back to `'authenticated'` when no role is stamped
   * (a fresh sign-up, an old session, or a JWT that hasn't been re-issued
   * since the role was set in app_metadata).
   */
  role: string;
  /** True while the session is being checked on first mount. */
  loading: boolean;
}

// AuthContext is exported alongside AuthProvider — standard React pattern.
// react-refresh HMR still works; this is a false positive from the rule.
// eslint-disable-next-line react-refresh/only-export-components
export const AuthContext = createContext<AuthContextValue>({
  session: null,
  user: null,
  role: 'authenticated',
  loading: true,
});

/**
 * Read `app_metadata.role` from the Supabase user record. We treat this as
 * the source of truth even though it's also in the JWT — the user object
 * is what supabase-js parses for us, and routing guards that need finer
 * mfa/factor claims decode the access_token themselves.
 */
function deriveRole(session: Session | null): string {
  const metadata = session?.user?.app_metadata as
    | { role?: unknown }
    | undefined;
  const role = metadata?.role;
  if (typeof role === 'string' && role.length > 0) return role;
  return 'authenticated';
}

/**
 * Point the query cache at whoever is signed in now.
 *
 * Must run BEFORE the new session reaches React state, or the first render
 * under the new identity can read cache entries belonging to the previous one —
 * which is the cross-tenant read this scoping exists to prevent. Called on
 * hydration and on every auth event; TOKEN_REFRESHED is the one that matters
 * after switch-tenant, because that is when the new tenant_id arrives.
 */
function pointQueryCacheAt(session: Session | null): void {
  const metadata = session?.user?.app_metadata as { tenant_id?: unknown } | undefined;
  const tenantId = typeof metadata?.tenant_id === 'string' ? metadata.tenant_id : null;
  setQueryPrincipal({ userId: session?.user?.id ?? null, tenantId });
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;

    // 1. Hydrate. supabase-js reads from localStorage synchronously inside
    //    its own initializer, but getSession() is async + returns the
    //    canonical value (including any refresh that's already in flight).
    void supabase.auth.getSession().then(({ data }) => {
      if (!mounted) return;
      // BUG-3: reconcile the persisted b2c onboarding stores against the
      // authenticated user BEFORE any onboarding screen reads them, so a new /
      // different user on this browser never inherits the previous user's
      // provisioned/subscriptionDone gates or stale doc-dump state.
      resetOnboardingStoresOnUserChange(data.session?.user?.id ?? null);
      pointQueryCacheAt(data.session);
      setSession(data.session);
      setLoading(false);
    });

    // 2. Subscribe. Listener fires on SIGNED_IN / SIGNED_OUT / TOKEN_REFRESHED
    //    / USER_UPDATED / PASSWORD_RECOVERY events. We update on all of them
    //    — there's no benefit to filtering, and the cost is negligible.
    //    SIGNED_OUT carries nextSession === null, which collapses `user` and
    //    `role` back to their signed-out defaults via the useMemo below; the
    //    /app + /admin + /superadmin layout guards then redirect to login on
    //    their next render. Centralising the redirect in the layouts (rather
    //    than imperatively in the listener) keeps the provider router-free.
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, nextSession) => {
      if (!mounted) return;
      // BUG-3: on SIGNED_IN as a different user (incl. a fresh signup after a
      // previous user used this browser) wipe the global onboarding stores; on
      // SIGNED_OUT this is a no-op so the same user can resume on return.
      resetOnboardingStoresOnUserChange(nextSession?.user?.id ?? null);
      pointQueryCacheAt(nextSession);
      setSession(nextSession);
      setLoading(false);
    });

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, []);

  // Memoise the context value so consumers don't re-render on every parent
  // render — only when session/loading actually change.
  const value = useMemo<AuthContextValue>(() => ({
    session,
    user: session?.user ?? null,
    role: deriveRole(session),
    loading,
  }), [session, loading]);

  return <AuthContext value={value}>{children}</AuthContext>;
}
